import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  IconButton,
  Badge,
  Divider,
  Button,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import DateTimeInput from '../../inputs/DateTimeInput';
import { FilterList, RestartAlt, Warning, Add, Delete, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Considerations, defaultConsiderations } from '../../../models/considerations';
import { PotentialOffersFilter } from '../../../models/potentialOffersFilter';
import { SearchFilterMode, VehicleAutoSearch, mapAutoSearchModeToFilterMode } from '../../../models/vehicleAutoSearch';
import { COUNTRY_NAMES, CountryCode } from '../../../models/country';
import { COUNTRY_TO_FLAG } from '../../../utils/languageUtils';
import { useTenant } from '../../../hooks';
import { FilterKey, OfferStatus } from '../../../types';
import { fetchContacts } from '../../../services/vehicleService';

// Create country options outside component to prevent recreation on every render
const COUNTRY_OPTIONS = Object.entries(COUNTRY_NAMES).map(([code, name]) => ({
  code: code as CountryCode,
  name,
  flag: COUNTRY_TO_FLAG[code as CountryCode] || ''
}));

interface ConsiderationsFilterProps {
  considerations: Considerations;
  onConsiderationsChange: (considerations: Considerations) => void;
  filterMode?: SearchFilterMode;
  onFilterModeChange?: (mode: SearchFilterMode) => void;
  autoSearchSession?: VehicleAutoSearch | null;
  useAutoSearchSync?: boolean;
  onUseAutoSearchSyncChange?: (sync: boolean) => void;
  hiddenFilters?: Set<FilterKey>; // Set of filters sections keys to hide.
  selectedCountries?: string[];
  onSelectedCountriesChange?: (countries: string[]) => void;
  filters?: PotentialOffersFilter;
  onFiltersChange?: (filters: PotentialOffersFilter) => void;
  onUpdateAutoSearch?: () => Promise<void>;
}

const ConsiderationsFilter: React.FC<ConsiderationsFilterProps> = ({
  considerations,
  onConsiderationsChange,
  filterMode = 'default',
  onFilterModeChange,
  autoSearchSession,
  useAutoSearchSync = false,
  onUseAutoSearchSyncChange,
  hiddenFilters,
  selectedCountries = [],
  onSelectedCountriesChange,
  filters = {},
  onFiltersChange,
  onUpdateAutoSearch,
}) => {
  const { t } = useTranslation();
  const { tenant } = useTenant();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [considerationsExpanded, setConsiderationsExpanded] = useState<boolean>(false);
  const [resetKey, setResetKey] = useState<number>(0);
  const [isDateFilterEnabled, setIsDateFilterEnabled] = useState<boolean>(!!filters?.startTime || !!filters?.endTime);
  const [isStatusFilterChecked, setIsStatusFilterChecked] = useState<boolean>(filters?.status !== undefined);
  const [isOriginCountryFilterChecked, setIsOriginCountryFilterChecked] = useState<boolean>(filters?.originCountry !== undefined);
  const [isDestinationCountryFilterChecked, setIsDestinationCountryFilterChecked] = useState<boolean>(filters?.destinationCountry !== undefined);
  const [isContactFilterChecked, setIsContactFilterChecked] = useState<boolean>(filters?.contact !== undefined);

  const autoSearchFilterMode = mapAutoSearchModeToFilterMode(autoSearchSession);
  // Determine if filter mode is out of sync with auto-search session
  const [isOutOfSync, setIsOutOfSync] = useState(autoSearchSession && !useAutoSearchSync && autoSearchFilterMode !== filterMode);
  const [updateAutoSearchLoading, setUpdateAutoSearchLoading] = useState<boolean>(false);

  const handleUpdateAutoSearch = async (): Promise<void> => {
    if (!onUpdateAutoSearch) return;
    setUpdateAutoSearchLoading(true);
    try {
      await onUpdateAutoSearch();
      setIsOutOfSync(false);
    } finally {
      setUpdateAutoSearchLoading(false);
    }
  };

  // Keep date filter checkbox in sync with actual filter values and ensure dates are cleared when disabled
  useEffect(() => {
    if (!isDateFilterEnabled && (filters?.startTime || filters?.endTime)) {
      // If checkbox is unchecked but dates exist, clear them
      onFiltersChange?.({
        ...filters,
        startTime: undefined,
        endTime: undefined
      });
    }
  }, [isDateFilterEnabled, filters?.startTime, filters?.endTime, onFiltersChange]);

  const [contact, setContact] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (contact !== filters?.contact) {
      setContact(filters?.contact);
    }
  }, [filters]);

  const [contacts, setContacts] = useState<string[]>([]);
  useEffect(() => {
    if (!contact) {
      setContacts([]);
      return;
    }

    const timer = setTimeout(async () => {
      const results = await fetchContacts(contact);
      setContacts(results);
    }, 300);

    return () => clearTimeout(timer);
  }, [contact]);

  useEffect(() => { // Update selected countries when auto-search session changes
    if (
      !useAutoSearchSync ||
      autoSearchFilterMode !== 'selectedCountries' ||
      !autoSearchSession?.selectedCountries || isOutOfSync
    ) return;

    const current = selectedCountries ?? [];
    const next = autoSearchSession.selectedCountries;

    // Only update if different
    if (current.join(',') !== next.join(',')) {
      onSelectedCountriesChange?.(next);
    }
  }, [
    useAutoSearchSync,
    autoSearchFilterMode,
    autoSearchSession?.selectedCountries,
    selectedCountries,
    onSelectedCountriesChange
  ]);

  useEffect(() => { // Update fullTruckLoad when auto-search session changes
    if (
      !useAutoSearchSync ||
      autoSearchSession?.fullTruckLoad === undefined ||
      isOutOfSync
    ) return;

    const current = filters?.fullTruckLoad ?? false;
    const next = autoSearchSession.fullTruckLoad ?? false;

    // Only update if different
    if (current !== next) {
      onFiltersChange?.({
        ...filters,
        fullTruckLoad: next ? true : undefined
      });
    }
  }, [
    useAutoSearchSync,
    autoSearchSession?.fullTruckLoad,
    filters?.fullTruckLoad,
    filters,
    isOutOfSync,
    onFiltersChange
  ]);

  useEffect(() => { // Update loading time window when auto-search session changes
    if (
      !useAutoSearchSync ||
      isOutOfSync ||
      !autoSearchSession
    ) return;

    const currentStartTime = filters?.startTime instanceof Date ? filters.startTime.getTime() : undefined;
    const currentEndTime = filters?.endTime instanceof Date ? filters.endTime.getTime() : undefined;
    const nextStartTime = autoSearchSession.startLoadingTime ? new Date(autoSearchSession.startLoadingTime).getTime() : undefined;
    const nextEndTime = autoSearchSession.endLoadingTime ? new Date(autoSearchSession.endLoadingTime).getTime() : undefined;

    // Only update if different
    if (currentStartTime !== nextStartTime || currentEndTime !== nextEndTime) {
      const newFilters = { ...filters };

      if (nextStartTime !== undefined) {
        const startDate = new Date(nextStartTime);
        startDate.setHours(0, 0, 0, 0);
        newFilters.startTime = startDate;
      } else {
        newFilters.startTime = undefined;
      }

      if (nextEndTime !== undefined) {
        const endDate = new Date(nextEndTime);
        endDate.setHours(23, 59, 59, 999);
        newFilters.endTime = endDate;
      } else {
        newFilters.endTime = undefined;
      }

      // Update checkbox state if times are present
      const shouldEnable = nextStartTime !== undefined || nextEndTime !== undefined;
      if (shouldEnable !== isDateFilterEnabled) {
        setIsDateFilterEnabled(shouldEnable);
      }

      onFiltersChange?.(newFilters);
    }
  }, [
    useAutoSearchSync,
    autoSearchSession?.startLoadingTime,
    autoSearchSession?.endLoadingTime,
    isOutOfSync
  ]);

  useEffect(() => { // Update out-of-sync state when dependencies change
    setIsOutOfSync(
      autoSearchSession
        ? (!useAutoSearchSync || autoSearchFilterMode !== filterMode)
        : false
    );
  }, [useAutoSearchSync, autoSearchFilterMode, filterMode]);

  // Handle sync checkbox change
  const handleSyncChange = (checked: boolean) => {
    if (onUseAutoSearchSyncChange) {
      onUseAutoSearchSyncChange(checked);
    }
    // Update out-of-sync state
    setIsOutOfSync(!checked);
    // When enabling sync, also update filter mode to match auto-search
    if (checked && autoSearchFilterMode && onFilterModeChange) {
      onFilterModeChange(autoSearchFilterMode);

      // If syncing to selectedCountries mode, also sync the countries from auto-search session
      if (autoSearchFilterMode === 'selectedCountries' && autoSearchSession?.selectedCountries && onSelectedCountriesChange) {
        onSelectedCountriesChange(autoSearchSession.selectedCountries);
      }

      // Sync fullTruckLoad and loading times from auto-search session
      if (onFiltersChange && autoSearchSession) {
        const newFilters = { ...filters };

        // Sync fullTruckLoad
        if (autoSearchSession.fullTruckLoad !== undefined) {
          newFilters.fullTruckLoad = autoSearchSession.fullTruckLoad ? true : undefined;
        }

        // Sync loading times
        if (autoSearchSession.startLoadingTime !== undefined) {
          const startDate = new Date(autoSearchSession.startLoadingTime);
          startDate.setHours(0, 0, 0, 0);
          newFilters.startTime = startDate;
        } else {
          newFilters.startTime = undefined;
        }

        if (autoSearchSession.endLoadingTime !== undefined) {
          const endDate = new Date(autoSearchSession.endLoadingTime);
          endDate.setHours(23, 59, 59, 999);
          newFilters.endTime = endDate;
        } else {
          newFilters.endTime = undefined;
        }

        // Update checkbox state
        const shouldEnable = autoSearchSession.startLoadingTime !== undefined || autoSearchSession.endLoadingTime !== undefined;
        setIsDateFilterEnabled(shouldEnable);

        onFiltersChange(newFilters);
      }
    }
  };

  // Handle filter mode change - untick sync checkbox when manually changed
  const handleFilterModeChange = (mode: SearchFilterMode) => {
    if (onFilterModeChange) {
      onFilterModeChange(mode);
    }
    // Untick sync checkbox when user manually changes filter mode
    if (onUseAutoSearchSyncChange && useAutoSearchSync) {
      onUseAutoSearchSyncChange(false);
    }
  };

  // Handle country selection changes
  const handleCountryChange = (index: number, countryCode: string) => {
    if (!onSelectedCountriesChange) return;
    const newCountries = selectedCountries.length > 0 ? [...selectedCountries] : [];
    newCountries[index] = countryCode;
    onSelectedCountriesChange(newCountries);

    setIsOutOfSync(true); // Mark as out of sync when user changes countries
  };

  const handleAddCountry = () => {
    if (!onSelectedCountriesChange) return;
    onSelectedCountriesChange([...selectedCountries, '']);

    setIsOutOfSync(true); // Mark as out of sync when user adds a country
  };

  const handleRemoveCountry = (index: number) => {
    if (!onSelectedCountriesChange) return;
    const newCountries = selectedCountries.filter((_, i) => i !== index);
    onSelectedCountriesChange(newCountries.length > 0 ? newCountries : ['']);

    setIsOutOfSync(true); // Mark as out of sync when user removes a country
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleChange = (key: keyof Considerations) => {
    onConsiderationsChange({
      ...considerations,
      [key]: !considerations[key]
    });
  };

  const handleReset = () => {
    onConsiderationsChange(defaultConsiderations);
    // Clear date filters and force inputs to remount
    onFiltersChange?.({
      ...filters,
      startTime: undefined,
      endTime: undefined
    });
    setIsDateFilterEnabled(false);
    setResetKey((prev) => prev + 1);
    handleSyncChange(true);
  };

  const activeFiltersCount = Object.values(considerations).filter(v => !v).length;

  const filterGroups = [
    {
      title: t('Location Filters'),
      filters: [
        { key: 'newStart' as keyof Considerations, label: t('New Start Location'), description: t('Filter by loading location proximity') },
        { key: 'newEnd' as keyof Considerations, label: t('New End Location'), description: t('Filter by unloading location proximity') },
        { key: 'whereAt' as keyof Considerations, label: t('Booking End Time'), description: t('Filter by booking unloading place end time') }
      ]
    },
    {
      title: t('Regulatory & Capacity'),
      filters: [
        { key: 'remainingCabotages' as keyof Considerations, label: t('Cabotage Rules'), description: t('Check remaining cabotage operations') },
        { key: 'dimensions' as keyof Considerations, label: t('Dimensions'), description: t('Check weight and length compatibility') }
      ]
    },
    {
      title: t('Vehicle Compatibility'),
      filters: [
        { key: 'body' as keyof Considerations, label: t('Body Type'), description: t('Match trailer body type') },
        { key: 'type' as keyof Considerations, label: t('Vehicle Type'), description: t('Match vehicle type compatibility') },
        { key: 'equipment' as keyof Considerations, label: t('Equipment'), description: t('Check required equipment') }
      ]
    },
    {
      title: t('Business Rules'),
      filters: [
        { key: 'lists' as keyof Considerations, label: t('White/Black Lists'), description: t('Apply domain white/black lists') }
      ]
    }
  ];

  // Show warning badge when out of sync with auto-search (but not when hiding auto-search sync)
  const showWarningBadge = !hiddenFilters?.has('autoSearchSync') && isOutOfSync;

  return (
    <>
      <Tooltip title={!hiddenFilters?.has('autoSearchSync') && isOutOfSync ? t('Filter mode differs from auto-search session') : t('Filter Options')}>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{
            p: 0.5,
            borderRadius: '50%',
            bgcolor: open ? 'border.blueLight' : 'background.grey',
            '&:hover': {
              bgcolor: open ? 'border.blueLight' : 'border.divider'
            }
          }}
        >
          <Badge
            badgeContent={showWarningBadge ? <Warning sx={{ fontSize: (theme: any) => theme.typography.fontSizes.caption }} /> : activeFiltersCount}
            color={showWarningBadge ? 'warning' : 'error'}
            invisible={activeFiltersCount === 0 && !showWarningBadge}
          >
            <FilterList sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body, color: open ? 'border.blue' : (showWarningBadge ? 'warning.main' : '#666') }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            minWidth: 400,
            maxWidth: 500,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            borderRadius: 2
          }
        }}
      >
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.dark' }}>
            {t('Filter Options')}
          </Typography>
          <Button
            size="small"
            startIcon={<RestartAlt />}
            onClick={handleReset}
            sx={{
              textTransform: 'none',
              fontSize: (theme: any) => theme.typography.fontSizes.small,
              color: 'text.grey',
              '&:hover': {
                bgcolor: 'background.grey'
              }
            }}
          >
            {t('Reset All')}
          </Button>
        </Box>

        <Typography variant="body2" sx={{ color: 'text.grey', mb: 2, fontSize: (theme: any) => theme.typography.fontSizes.body }}>
          {t('Uncheck filters to exclude them from the matching criteria. Disabled filters will show more offers.')}
        </Typography>

        {/* Auto-search sync checkbox with tooltip */}
        {autoSearchSession && onUseAutoSearchSyncChange && !hiddenFilters?.has('autoSearchSync') && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Tooltip
              title={t(
                "When enabled, Search Filter Mode follows this vehicle's auto-search settings. Uncheck to override manually."
              )}
              enterDelay={300}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useAutoSearchSync && !isOutOfSync}
                    onChange={(e) => handleSyncChange(e.target.checked)}
                    size="small"
                    inputProps={{ 'aria-describedby': 'auto-search-sync-desc' }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: (theme: any) => theme.typography.fontSizes.body,
                      color: useAutoSearchSync && !isOutOfSync ? 'text.darkGrey' : 'error.main'
                    }}
                  >
                    {t('Sync with auto-search session')}
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Tooltip>
            {onUpdateAutoSearch && isOutOfSync && (
              <Tooltip 
                title={t('Your manual filters differ from the auto-search session. Click to sync the auto-search with these filters.')}
                enterDelay={300}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={handleUpdateAutoSearch}
                  disabled={updateAutoSearchLoading}
                  sx={{
                    textTransform: 'none',
                    fontSize: (theme: any) => theme.typography.fontSizes.small,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    fontWeight: 600,
                  }}
                >
                  {updateAutoSearchLoading ? t('Syncing...') : t('Apply filters to auto-search')}
                </Button>
              </Tooltip>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Search Filter Mode Selector */}
        {onFilterModeChange && (
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body }}>{t('Search Filter Mode')}</InputLabel>
              <Select
                value={filterMode}
                label={t('Search Filter Mode')}
                onChange={(e) => handleFilterModeChange(e.target.value as SearchFilterMode)}
                sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body }}
              >
                <MenuItem value="default">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('Default (No Filter)')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('Show all potential offers without destination preference filtering')}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="preferredDestinations">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('Preferred Destinations')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('Use configured preferred destinations')}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="returnDestinations">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('Return Destinations')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('Use return destinations to home base')}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="preferredRoute">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('Circle Trip')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('Use preferred route configuration')}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="selectedCountries">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('Selected Countries')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('Search only in specific countries')}
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Country Selection UI for selectedCountries filter mode */}
            {filterMode === 'selectedCountries' && onSelectedCountriesChange && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: (theme: any) => theme.typography.fontSizes.body }}>
                    {t('Countries')}
                  </Typography>
                  <Chip
                    label={`${selectedCountries.filter(c => c).length} ${t('selected')}`}
                    size="small"
                    sx={{
                      color: 'text.white',
                      backgroundColor: 'text.purple',
                      fontWeight: 600,
                      fontSize: (theme: any) => theme.typography.fontSizes.caption
                    }}
                  />
                </Box>

                {(selectedCountries.length > 0 ? selectedCountries : ['']).map((countryCode, index) => {
                  const countryOption = COUNTRY_OPTIONS.find(c => c.code === countryCode);
                  return (
                    <Box key={`${countryCode}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Autocomplete
                        value={countryOption || null}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            handleCountryChange(index, newValue.code);
                          }
                        }}
                        options={COUNTRY_OPTIONS}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        disableClearable={false}
                        renderOption={(props, option) => {
                          const { key, ...otherProps } = props;
                          return (
                            <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{option.flag}</span>
                              <span>{option.code} - {option.name}</span>
                            </Box>
                          );
                        }}
                        slotProps={{
                          popper: {
                            placement: 'bottom-start',
                            modifiers: [
                              {
                                name: 'flip',
                                enabled: false,
                              },
                              {
                                name: 'preventOverflow',
                                enabled: true,
                              },
                            ],
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={t('Select country')}
                            size="small"
                            sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body }}
                          />
                        )}
                        sx={{ flex: 1, minWidth: 0 }}
                      />
                      {selectedCountries.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveCountry(index)}
                          sx={{
                            color: 'error.main',
                            '&:hover': { backgroundColor: 'error.lighter' }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}

                <Button
                  startIcon={<Add />}
                  onClick={handleAddCountry}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontSize: (theme: any) => theme.typography.fontSizes.body,
                    borderStyle: 'dashed',
                    color: 'text.purple',
                    borderColor: 'text.purple',
                    '&:hover': {
                      borderColor: 'text.purple',
                      backgroundColor: 'rgba(156, 39, 176, 0.04)',
                    }
                  }}
                >
                  {t('Add Country')}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {onFilterModeChange && <Divider sx={{ mb: 2 }} />}

        {/* Full Truck Load Filter */}
        <Box>
          <Tooltip
            title={t('Full truck load means offers with length ≥ {{minLength}}m. Change this in Tenant Settings.', { minLength: tenant?.minLengthMForFullTruckLoad ?? '?' })}
            placement="top"
            arrow
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters?.fullTruckLoad ?? false}
                  onChange={(e) => {
                    onFiltersChange?.({
                      ...filters,
                      fullTruckLoad: e.target.checked ? true : undefined
                    });
                    setIsOutOfSync(true); // Mark as out of sync when user changes fullTruckLoad
                  }}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('Full Truck Load')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Only show FTL offers (length ≥ {{minLength}}m)', { minLength: tenant?.minLengthMForFullTruckLoad ?? '?' })}
                  </Typography>
                </Box>
              }
              sx={{ mb: 1.5, ml: 0 }}
            />
          </Tooltip>
        </Box>

        {/* Start Time and End Time Filters */}
        <Box sx={{ borderRadius: 1 }}>
          <Tooltip
            title={t('Filter offers by when the loading can start. This shows only offers whose loading window overlaps with your selected dates.')}
            placement="top"
            arrow
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDateFilterEnabled}
                  onChange={(e) => {
                    setIsDateFilterEnabled(e.target.checked);
                    // Clear dates when disabled
                    if (!e.target.checked) {
                      onFiltersChange?.({
                        ...filters,
                        startTime: undefined,
                        endTime: undefined
                      });
                      setResetKey((prev) => prev + 1);
                    }
                    setIsOutOfSync(true); // Mark as out of sync when user changes loading time window
                  }}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('Loading Time Window')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Filter offers by their loading time window')}
                  </Typography>
                </Box>
              }
              sx={{ mb: 1.5, ml: 0 }}
            />
          </Tooltip>

          {isDateFilterEnabled && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
              <DateTimeInput
                key={`start-time-${resetKey}`}
                label={t('Start Date')}
                type="date"
                size="small"
                fullWidth
                value={filters?.startTime ?? null}
                onChange={(value) => {
                  let date: Date | undefined;
                  if (value && typeof value === 'object' && 'toDate' in value) {
                    date = (value as any).toDate();
                  } else if (value) {
                    date = value as any as Date;
                  } else {
                    date = undefined;
                  }
                  // Set time to 00:00:00
                  if (date) {
                    date.setHours(0, 0, 0, 0);
                  }
                  onFiltersChange?.({
                    ...filters,
                    startTime: date
                  });
                  setIsOutOfSync(true); // Mark as out of sync when user changes start date
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body }}
              />
              <DateTimeInput
                key={`end-time-${resetKey}`}
                label={t('End Date')}
                type="date"
                size="small"
                fullWidth
                value={filters?.endTime ?? null}
                onChange={(value) => {
                  let date: Date | undefined;
                  if (value && typeof value === 'object' && 'toDate' in value) {
                    date = (value as any).toDate();
                  } else if (value) {
                    date = value as any as Date;
                  } else {
                    date = undefined;
                  }
                  // Set time to 23:59:59
                  if (date) {
                    date.setHours(23, 59, 59, 999);
                  }
                  onFiltersChange?.({
                    ...filters,
                    endTime: date
                  });
                  setIsOutOfSync(true); // Mark as out of sync when user changes end date
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body }}
              />
            </Box>
          )}
        </Box>

        {/* Status */}
        {!hiddenFilters?.has('status') && <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isStatusFilterChecked}
                onChange={(e) => {
                  setIsStatusFilterChecked(e.target.checked);
                  if (!e.target.checked) {
                    onFiltersChange?.({
                      ...filters,
                      status: undefined,
                    });
                  }
                }}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('Status')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('Filter offers by their status')}
                </Typography>
              </Box>
            }
            sx={{ mb: 1.5, ml: 0 }}
          />
          {isStatusFilterChecked &&
            <Select
              label="Status"
              value={filters?.status ?? ''}
              onChange={(e) => {
                onFiltersChange?.({
                  ...filters,
                  status: Number(e.target.value),
                });
              }}
            >
              {Object.entries(OfferStatus)
                .filter(([key]) => isNaN(Number(key)))
                .map(([status, value]) => (
                  <MenuItem value={value} key={status}>
                    {status}
                  </MenuItem>
                ))}
            </Select>
          }
        </Box>}

        {/* Origin country */}
        {!hiddenFilters?.has('originCountry') && <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isOriginCountryFilterChecked}
                onChange={(e) => {
                  setIsOriginCountryFilterChecked(e.target.checked);
                  if (!e.target.checked) {
                    onFiltersChange?.({
                      ...filters,
                      originCountry: undefined
                    });
                  }
                }}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('Origin country')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('Filter offers by their origin country')}
                </Typography>
              </Box>
            }
            sx={{ mb: 1.5, ml: 0 }}
          />
          {isOriginCountryFilterChecked &&
            <Autocomplete
              value={COUNTRY_OPTIONS.find(c => c.code === filters.originCountry) || null}
              onChange={(_, newValue) => {
                if (newValue) {
                  onFiltersChange?.({
                    ...filters,
                    originCountry: newValue.code,
                  });
                }
              }}
              options={COUNTRY_OPTIONS}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{option.flag}</span>
                  <span>{option.code} - {option.name}</span>
                </Box>
              )}
              slotProps={{
                popper: {
                  placement: 'bottom-start',
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false,
                    },
                    {
                      name: 'preventOverflow',
                      enabled: true,
                    },
                  ],
                },
              }}
              renderInput={(params) => {
                const selectedOption = COUNTRY_OPTIONS.find(c => c.code === filters.originCountry);
                return (
                  <TextField
                    {...params}
                    label={t('Country')}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedOption?.flag ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          <span>{selectedOption.flag}</span>
                        </Box>
                      ) : null,
                    }}
                  />
                );
              }}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              sx={{ minWidth: 200 }}
              size="small"
              onClick={(e) => e.stopPropagation()}
            />
          }
        </Box>}

        {/* Destination country */}
        {!hiddenFilters?.has('destinationCountry') && <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isDestinationCountryFilterChecked}
                onChange={(e) => {
                  setIsDestinationCountryFilterChecked(e.target.checked);
                  if (!e.target.checked) {
                    onFiltersChange?.({
                      ...filters,
                      destinationCountry: undefined
                    });
                  }
                }}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('Destination country')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('Filter offers by their destination country')}
                </Typography>
              </Box>
            }
            sx={{ mb: 1.5, ml: 0 }}
          />
          {isDestinationCountryFilterChecked &&
            <Autocomplete
              value={COUNTRY_OPTIONS.find(c => c.code === filters.destinationCountry) || null}
              onChange={(_, newValue) => {
                if (newValue) {
                  onFiltersChange?.({
                    ...filters,
                    destinationCountry: newValue.code,
                  });
                }
              }}
              options={COUNTRY_OPTIONS}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{option.flag}</span>
                  <span>{option.code} - {option.name}</span>
                </Box>
              )}
              slotProps={{
                popper: {
                  placement: 'bottom-start',
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false,
                    },
                    {
                      name: 'preventOverflow',
                      enabled: true,
                    },
                  ],
                },
              }}
              renderInput={(params) => {
                const selectedOption = COUNTRY_OPTIONS.find(c => c.code === filters.destinationCountry);
                return (
                  <TextField
                    {...params}
                    label={t('Country')}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedOption?.flag ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          <span>{selectedOption.flag}</span>
                        </Box>
                      ) : null,
                    }}
                  />
                );
              }}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              sx={{ minWidth: 200 }}
              size="small"
              onClick={(e) => e.stopPropagation()}
            />
          }
        </Box>}

        {/* Contact */}
        {!hiddenFilters?.has('contact') && <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isContactFilterChecked}
                onChange={(e) => {
                  setIsContactFilterChecked(e.target.checked);
                  if (!e.target.checked) {
                    onFiltersChange?.({
                      ...filters,
                      contact: undefined
                    });
                  }
                }}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('Contact')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('Filter offers by their contact')}
                </Typography>
              </Box>
            }
            sx={{ mb: 1.5, ml: 0 }}
          />
          {isContactFilterChecked &&
            <Autocomplete
              freeSolo={true}
              options={contacts}
              value={contact ?? ''}
              renderInput={(params) => <TextField
                onChange={(e) => setContact(e.target.value as string | undefined)}
                onBlur={(e) => {
                  const value = e.target.value as string | undefined;
                  onFiltersChange?.({
                    ...filters,
                    contact: value !== '' ? value : undefined,
                  });
                }}
                {...params} label="Contact"
              />}
            />
          }
        </Box>}

        <Divider sx={{ mb: 2, mt: 2 }} />

        {/* Considerations Accordion */}
        <Accordion
          expanded={considerationsExpanded}
          onChange={(e, isExpanded) => setConsiderationsExpanded(isExpanded)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography sx={{ fontWeight: 600, fontSize: (theme: any) => theme.typography.fontSizes.body }}>
              {t('Considerations')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {filterGroups.map((group, groupIndex) => (
              <Box key={groupIndex} sx={{ mb: 2, p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.dark', mb: 1, fontSize: (theme: any) => theme.typography.fontSizes.body }}>
                  {group.title}
                </Typography>
                <FormGroup>
                  {group.filters.map((filter) => (
                    <Tooltip key={filter.key} title={filter.description} placement="left" arrow>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={considerations[filter.key]}
                            onChange={() => handleChange(filter.key)}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body, color: 'text.darkGrey' }}>
                            {filter.label}
                          </Typography>
                        }
                        sx={{
                          ml: 0,
                          mb: 0.5,
                          '&:hover': {
                            bgcolor: 'background.grey',
                            borderRadius: 1
                          }
                        }}
                      />
                    </Tooltip>
                  ))}
                </FormGroup>
                {groupIndex < filterGroups.length - 1 && <Divider sx={{ mt: 1, mb: 1 }} />}
              </Box>
            ))}
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.warning', borderRadius: 1, border: (theme: any) => `1px solid ${theme.palette.text.warningOrange}` }}>
              <Typography variant="caption" sx={{ color: (theme: any) => theme.palette.text.warningOrange, fontSize: (theme: any) => theme.typography.fontSizes.small }}>
                <strong>{t('Note:')}</strong> {t('Disabling considerations may increase results but reduce relevance. All filters are enabled by default for optimal matching.')}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Popover >
    </>
  );
};

export default ConsiderationsFilter;
